
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import countries from "@/data/countries";
import { regions } from "@/data/regions";
import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";

interface CountryRegionFieldsProps {
  form: UseFormReturn<any>;
  selectedCountry: string;
  setSelectedCountry: (country: string) => void;
  setAddressValidated: (value: boolean) => void;
  setSelectedLocation: (location: any | null) => void;
}

export const CountryRegionFields = ({
  form,
  selectedCountry,
  setSelectedCountry,
  setAddressValidated,
  setSelectedLocation
}: CountryRegionFieldsProps) => {
  const [availableRegions, setAvailableRegions] = useState(regions.filter(r => r.country === selectedCountry));
  const { language, t } = useLanguage();
  
  // 當國家改變時更新可用的地區選項
  useEffect(() => {
    const filteredRegions = regions.filter(r => r.country === selectedCountry);
    setAvailableRegions(filteredRegions);
    
    // 如果當前選擇的地區不屬於新選擇的國家，則重置地區選擇
    const currentRegion = form.getValues('region');
    const isValidRegion = filteredRegions.some(r => r.code === currentRegion);
    if (currentRegion && !isValidRegion) {
      form.setValue('region', '');
    }
  }, [selectedCountry, form]);
  
  const onCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    
    // 重置地址相關狀態
    setAddressValidated(false);
    setSelectedLocation(null);
  };
  
  const RequiredIndicator = () => (
    <span className="text-destructive ml-1">*</span>
  );

  // Get country name based on current language
  const getCountryName = (code: string) => {
    const country = countries.find(c => c.code === code);
    if (!country) return code;
    
    // Return English name if language is English, otherwise return Chinese name
    if (language === 'en') {
      const englishNames: Record<string, string> = {
        'TW': 'Taiwan',
        'CN': 'China',
        'HK': 'Hong Kong',
        'MO': 'Macau',
        'JP': 'Japan',
        'KR': 'South Korea',
        'SG': 'Singapore',
        'MY': 'Malaysia',
        'US': 'United States',
        'CA': 'Canada',
        'GB': 'United Kingdom',
        'FR': 'France',
        'DE': 'Germany',
        'AU': 'Australia',
        'NZ': 'New Zealand'
      };
      return englishNames[code] || country.name;
    }
    
    // Return full Chinese country names
    const chineseNames: Record<string, string> = {
      'TW': '台灣',
      'CN': '中國',
      'HK': '香港',
      'MO': '澳門',
      'JP': '日本',
      'KR': '韓國',
      'SG': '新加坡',
      'MY': '馬來西亞',
      'US': '美國',
      'CA': '加拿大',
      'GB': '英國',
      'FR': '法國',
      'DE': '德國',
      'AU': '澳洲',
      'NZ': '紐西蘭'
    };
    
    return chineseNames[code] || country.name;
  };
  
  // Get region name based on current language
  const getRegionName = (code: string) => {
    const region = regions.find(r => r.code === code);
    if (!region) return code;
    
    // Return English name if language is English, otherwise return Chinese name
    if (language === 'en') {
      const englishNames: Record<string, string> = {
        // Canada provinces and territories
        'AB': 'Alberta',
        'BC': 'British Columbia',
        'MB': 'Manitoba',
        'NB': 'New Brunswick',
        'NL': 'Newfoundland and Labrador',
        'NS': 'Nova Scotia',
        'NT': 'Northwest Territories',
        'NU': 'Nunavut',
        'ON': 'Ontario',
        'PE': 'Prince Edward Island',
        'QC': 'Quebec',
        'SK': 'Saskatchewan',
        'YT': 'Yukon',
        // US states
        'AL': 'Alabama',
        'AK': 'Alaska',
        'AZ': 'Arizona',
        'AR': 'Arkansas',
        'CA': 'California',
        'CO': 'Colorado',
        'CT': 'Connecticut',
        'DE': 'Delaware',
        'FL': 'Florida',
        'GA': 'Georgia',
        'HI': 'Hawaii',
        'ID': 'Idaho',
        'IL': 'Illinois',
        'IN': 'Indiana',
        'IA': 'Iowa',
        'KS': 'Kansas',
        'KY': 'Kentucky',
        'LA': 'Louisiana',
        'ME': 'Maine',
        'MD': 'Maryland',
        'MA': 'Massachusetts',
        'MI': 'Michigan',
        'MN': 'Minnesota',
        'MS': 'Mississippi',
        'MO': 'Missouri',
        'MT': 'Montana',
        'NE': 'Nebraska',
        'NV': 'Nevada',
        'NH': 'New Hampshire',
        'NJ': 'New Jersey',
        'NM': 'New Mexico',
        'NY': 'New York',
        'NC': 'North Carolina',
        'ND': 'North Dakota',
        'OH': 'Ohio',
        'OK': 'Oklahoma',
        'OR': 'Oregon',
        'PA': 'Pennsylvania',
        'RI': 'Rhode Island',
        'SC': 'South Carolina',
        'SD': 'South Dakota',
        'TN': 'Tennessee',
        'TX': 'Texas',
        'UT': 'Utah',
        'VT': 'Vermont',
        'VA': 'Virginia',
        'WA': 'Washington',
        'WV': 'West Virginia',
        'WI': 'Wisconsin',
        'WY': 'Wyoming',
        'DC': 'District of Columbia',
      };
      return englishNames[code] || region.name;
    }
    
    return region.name;
  };
  
  return (
    <>
      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {language === 'en' ? 'Country' : '國家'}
              <RequiredIndicator />
            </FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                onCountryChange(value);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'en' ? 'Select a country' : '選擇國家'} />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-50 max-h-80">
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {getCountryName(country.code)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="region"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {language === 'en' ? 'Region' : '地區'}
              <RequiredIndicator />
            </FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={availableRegions.length === 0}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={
                    availableRegions.length > 0 
                      ? (language === 'en' ? 'Select a region' : '選擇地區')
                      : (language === 'en' ? 'No regions available for selected country' : '所選國家無地區資料')
                  } />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-50 max-h-80">
                {availableRegions.length > 0 ? (
                  availableRegions.map((region) => (
                    <SelectItem key={region.code} value={region.code}>
                      {getRegionName(region.code)}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>
                    {language === 'en' ? 'No regions available for selected country' : '該國家無可用地區資料'}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
